import { Button } from "@/components/ui/button"
import { DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import type { DiagnosticActivity } from "@/services/api/odin/activities/diagnostic/study-session/update"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  type UpdateAnalysisFormValues,
  updateAnalysisSchema,
} from "../../validations/update-analysis"
import { useUpdateDiagnostic } from "../../queries/use-update-diagnostic"
import { useParams } from "react-router-dom"

interface AnalysisFormProps {
  activity: DiagnosticActivity
  onUpdateActivity: (question: string, options?: string[], answer?: string) => void
  onClose: () => void
}

export default function AnalysisForm({ activity, onUpdateActivity, onClose }: AnalysisFormProps) {
  const { mutateAsync: updateDiagnostic, isPending } = useUpdateDiagnostic()
  const { id } = useParams()

  const form = useForm<UpdateAnalysisFormValues>({
    defaultValues: {
      question: activity.next?.question || "",
      answer: activity.next?.answer || "",
    },
    resolver: zodResolver(updateAnalysisSchema),
  })

  async function onSubmit(data: UpdateAnalysisFormValues) {
    await updateDiagnostic({
      id: id as string,
      question: data.question,
      index: activity.current,
      payload: {
        answer: data.answer,
      },
    })
    onUpdateActivity(data.question, undefined, data.answer)
    onClose()
  }

  return (
    <>
      <DialogHeader className="flex flex-row items-center justify-between border-b border-neutral-200 pb-4 pl-4">
        <DialogTitle>Editar pregunta</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <ScrollArea className="h-[200px]">
            <div className="space-y-4 px-6">
              <FormField
                control={form.control}
                name="question"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enunciado</FormLabel>
                    <FormControl>
                      <Input disabled={isPending} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="answer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Respuesta esperada</FormLabel>
                    <FormControl>
                      <Textarea
                        disabled={isPending}
                        className="h-auto min-h-[60px] w-full"
                        rows={6}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </ScrollArea>
          <DialogFooter className="border-t border-neutral-200 px-4">
            <Button type="submit" className="self-end bg-primary-500" disabled={isPending}>
              Guardar cambios
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  )
}
